import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { PHOTOS_STEP_BODY_LOCK_CLASS } from './usePhotosStepBodyScrollLock';
import { wizardFontStyle } from './wizardTypography';

/**
 * Fullscreen photo viewer with keyboard and click-outside close.
 * @param {{ photos: Array<{ url?: string, preview?: string, label?: string }>, index: number, onClose: () => void, onNavigate: (step: number) => void }} props
 */
export default function PhotoLightbox({ photos, index, onClose, onNavigate }) {
    const photo = photos[index];

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') onNavigate(-1);
            if (e.key === 'ArrowRight') onNavigate(1);
        };
        window.addEventListener('keydown', handleKeyDown);
        const photosStepLocksBody = document.body.classList.contains(PHOTOS_STEP_BODY_LOCK_CLASS);
        const prevOverflow = document.body.style.overflow;
        if (!photosStepLocksBody) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            if (!photosStepLocksBody) {
                document.body.style.overflow = prevOverflow;
            }
        };
    }, [onClose, onNavigate]);

    if (!photo) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/85"
            style={wizardFontStyle}
            role="dialog"
            aria-modal="true"
            aria-label="Photo preview"
            onClick={onClose}
        >
            <button
                type="button"
                className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white hover:bg-black/60 transition-colors"
                aria-label="Previous photo"
                onClick={(e) => { e.stopPropagation(); onNavigate(-1); }}
            >
                <ChevronLeftIcon className="h-8 w-8 sm:h-10 sm:w-10" />
            </button>

            <div
                className="relative max-h-[90vh] max-w-[min(92vw,1200px)] px-14 sm:px-20"
                onClick={e => e.stopPropagation()}
            >
                <img
                    src={photo.url || photo.preview}
                    alt={photo.label ?? 'Photo'}
                    className="max-h-[85vh] max-w-full object-contain rounded-lg shadow-2xl"
                />
                <p className="mt-3 text-center text-sm text-white/70">
                    {index + 1} of {photos.length}
                </p>
            </div>

            <button
                type="button"
                className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white hover:bg-black/60 transition-colors"
                aria-label="Next photo"
                onClick={(e) => { e.stopPropagation(); onNavigate(1); }}
            >
                <ChevronRightIcon className="h-8 w-8 sm:h-10 sm:w-10" />
            </button>
        </div>,
        document.body
    );
}
